import { PlanyoService } from '@/api'
import Storage from '../../helpers/localStorage'
import date from 'date-and-time'
import { startDate as initStartDate, minimumNumberOfWeeks } from '@/helpers/dataPickerSettings'

// state
const state = () => ({
  orderSteps: [
    {
      value: 'movingType',
      label: 'Wohnung',
    },
    {
      value: 'userNeeds',
      label: 'ihren Bedarf',
    },
    {
      value: 'productsSelection',
      label: 'Zubehör',
    },
    {
      value: 'userAddress',
      label: 'Adresse',
      children: [
        {
          value: 'deliveryType',
          label: 'Select Shop',
        },
        {
          value: 'userAddress',
          label: 'User Address',
        },
        {
          value: 'pickupDelivery',
          label: '',
        },
        {
          value: 'deliveryDelivery',
          label: '',
        },
      ],
    },
    {
      value: 'checkoutForm',
      label: 'Checkout',
    },
    {
      value: 'orderSummary',
      label: 'Summary',
    },
    {
      value: 'paymentForm',
      label: 'Payment',
    },
  ],
  orderStep: 1,
  orderChildStep: 0,
  movingType: 'apartment',
  startDate: initStartDate,
  amountOfWeeks: minimumNumberOfWeeks,
  deliveryType: null,
  deliveryProduct: [],
  remoteId: null,
  voucherCode: null,
  voucher: null,
  reservationsResponse: [],
  items: [],
  shops: [],
  pickUpPlaceType: null,
  pickUpPlace: null,
  pickUpStore: null,
  deliverPlaceID: null,
  maxPickUpPointForKiosk: 6, // TODO move this to wp and get value from api request
})

// getters
const getters = {
  orderStatus(state) {
    return state.orderSteps[state.orderStep].value
  },
  childStatus(state) {
    if (!state.orderSteps[state.orderStep].children) {
      return false
    } else {
      return state.orderSteps[state.orderStep].children[state.orderChildStep].value
    }
  },
  orderableProducts(state, getters, rootState) {
    return rootState.products.packages
      .filter((p) => {
        const orderProduct = state.items.find((v) => {
          return v.id === p.properties.main_product_id
        })

        if (
          orderProduct &&
          +p.quantity_available >= +orderProduct.quantity &&
          p.properties.storage_name === state.pickUpPlaceType
        ) {
          return true
        }

        return false
      })
      .map((p) => {
        const orderProduct = state.items.find((v) => v.id == p.properties.main_product_id)
        return {
          ...p,
          quantity: orderProduct.quantity,
        }
      })
  },
  endDate(state) {
    if (!state.startDate) return null
    return date.addDays(state.startDate, state.amountOfWeeks * 7)
  },
  capacityPoints(state, getters, rootState) {
    if (!state.items.length) return 0

    const reducer = (total, p) => {
      const product = rootState.products.base.find((v) => {
        return v.id === p.id
      })
      const step = +product.properties.quantity_step || 1
      const points = +product.properties.capacity_points_per_quantity_step || 0
      return total + (p.quantity / step) * points
    }

    const sum = state.items.reduce(reducer, 0)
    // const sum = getters.orderableProducts.reduce(reducer, 0)
    return sum
  },
  pickupPoints(state, getters, rootState, rootGetters) {
    if (!state.items.length) return 0

    const reducer = (total, p) => {
      const product = rootState.products.base.find((v) => {
        return v.id === p.id
      })

      const freeForDeliveryItem = rootGetters['products/withFreeShipment'].find((v) => v.id === p.id)
      const amountOfFreeDelivery = +freeForDeliveryItem?.amount || 0

      const step = +product.properties.quantity_step || 1
      const points = +product.properties.pickup_point_per_quantity_step || 0
      const amountOfItems = Math.max(0, p.quantity - amountOfFreeDelivery)
      return total + (amountOfItems / step) * points
    }

    const sum = state.items.reduce(reducer, 0)

    return Math.ceil(sum)
  },
  priceFrom(state, getters, rootState) {
    return state.items.reduce((sum, current) => {
      const product = rootState.products.base.find((v) => v.id === current.id)
      sum += current.quantity * +product.total_price
      return sum
    }, 0)
  },
  priceFinal(state, getters, rootState) {
    return getters.orderableProducts.reduce((sum, current) => {
      const product = rootState.products.packages.find((v) => v.id === current.id)
      sum += current.quantity * +product.total_price
      return sum
    }, 0)
  },
  currentCurrency(state, getters, rootState) {
    if (state.items.length <= 0) return 'CHF'

    const itemID = state.items[0].id
    const product = rootState.products.base.find((v) => v.id === itemID)
    return product.currency
  },
  isNextStepIsValid(state, getters, rootState) {
    // const elo = true
    // if (elo) return true
    // Date selecting
    if (state.orderStep === 1) {
      if (!!state.startDate && !!getters.endDate) {
        return true
      } else {
        return false
      }
    }

    // Items selector
    if (state.orderStep === 2) {
      return state.items.length > 0
    }

    // Delivery type
    if (state.orderStep === 3) {
      if (state.deliveryType === 'delivery') {
        return rootState.user.areAddressesSame
          ? !!rootState.user.shippingStreet && !!rootState.user.shippingPostCode && !!rootState.user.shippingCity
          : !!rootState.user.shippingStreet &&
              !!rootState.user.shippingPostCode &&
              !!rootState.user.shippingCity &&
              !!rootState.user.pickupStreet &&
              !!rootState.user.pickupPostCode &&
              !!rootState.user.pickupCity
      }

      if (state.deliveryType === 'pickup') {
        return !!state.pickUpPlace
      }
    }

    // Invoice and personal data
    if (state.orderStep === 4) {
      return (
        rootState.user.firstName &&
        rootState.user.lastName &&
        rootState.user.address &&
        rootState.user.city &&
        rootState.user.postCode &&
        rootState.user.country &&
        rootState.user.emailAddress &&
        rootState.user.mobile &&
        rootState.user.acceptTermsOfService
      )
    }

    // summary page
    if (state.orderStep === 5) {
      return rootState.user.acceptTermsOfService
    }
  },
  isKioskAvailable(state, getters) {
    return getters.pickupPoints <= state.maxPickUpPointForKiosk
  },
}

// actions
const actions = {
  async createRemoteCart({ state, commit, rootState }) {
    const resp = await PlanyoService.createCart({
      language: rootState.user.currentLanguage.toUpperCase(),
    })
    const remoteCartId = resp.data.cart_id

    if (!state.remoteId) {
      commit('setRemoteCartId', remoteCartId)
      Storage.setRemoteCartId(remoteCartId)
    }
  },
  clearRemoteCartId({ commit }) {
    const remoteCartId = null
    commit('setRemoteCartId', remoteCartId)
    Storage.setRemoteCartId(remoteCartId)
  },
  async makeReservation({ state, rootState, getters, rootGetters }) {
    let additionalDeliveryData = {}
    let voucherUsed = false
    let pickupStoreData = {}

    if (state.deliveryType === 'delivery') {
      additionalDeliveryData = {
        rental_prop_Shipping_Address: rootGetters['user/shippingAddress'],
        rental_prop_Shipping_Note: rootState.user.shippingNote,
        rental_prop_Pickup_Address: rootGetters['user/pickupAddress'],
        rental_prop_Pickup_Note: rootState.user.pickupNote,
      }
    }

    const reservation = {
      language: rootState.user.currentLanguage.toUpperCase(),

      start_time: date.format(state.startDate, 'YYYY-MM-DD'),
      end_time: date.format(getters.endDate, 'YYYY-MM-DD'),
      cart_id: state.remoteId,

      rental_prop_Andre: rootState.user.salutation,
      first_name: rootState.user.firstName,
      last_name: rootState.user.lastName,

      rental_prop_Company: rootState.user.company,
      address: rootState.user.address,
      city: rootState.user.city,
      zip: rootState.user.postCode,
      country: rootState.user.country,

      email: rootState.user.emailAddress,
      mobile_prefix: '41',
      mobile_number: rootState.user.mobile,
      user_notes: rootState.user.notes,

      rental_prop_SwissPass_Cart_ID: rootState.user.swissPassCardID,
      rental_prop_SwissPass_Customer_ID: rootState.user.swissPassCustomerID,
      rental_prop_SwissPass_Login: rootState.user.swissPassLogin,

      rental_prop_agreement: true,

      rental_prop_Delivery: `${state.deliveryType.toUpperCase()}: ${state.pickUpPlace || 'Home Delivery'}`,
      ...additionalDeliveryData,
    }

    if (state.pickUpPlace) {
      pickupStoreData = {
        rental_prop_Store_Hours_Mo: state.pickUpStore.openHours[0].time,
        rental_prop_Store_Hours_Tu: state.pickUpStore.openHours[1].time,
        rental_prop_Store_Hours_We: state.pickUpStore.openHours[2].time,
        rental_prop_Store_Hours_Th: state.pickUpStore.openHours[3].time,
        rental_prop_Store_Hours_Fr: state.pickUpStore.openHours[4].time,
        rental_prop_Store_Hours_Sa: state.pickUpStore.openHours[5].time,
        rental_prop_Store_Hours_Su: state.pickUpStore.openHours[6].time,
        rental_prop_Store_Gps_Lat: state.pickUpStore.lat,
        rental_prop_Store_Gps_Lng: state.pickUpStore.lng,
        rental_prop_Store_Address: state.pickUpStore.location,
        rental_prop_Store_Location: state.pickUpStore.extraLocation,
        rental_prop_Store_Brand: state.pickUpStore.brand,
      }
    }

    const products = [...getters.orderableProducts]
    const extraProducts = [...state.deliveryProduct, ...rootState.products.deposit]
    const allReservations = []

    products.forEach((product) => {
      const voucherPrice = {}

      if (
        (state.voucher && !state.voucher.resources) ||
        (state.voucher && state.voucher.resources && state.voucher.resources.split(',').indexOf(product.id) > -1)
      ) {
        if (state.voucher.discount_value.includes('%') || !voucherUsed) {
          voucherPrice['rental_prop_voucher'] = state.voucherCode
          voucherUsed = true
        }
      }

      const reservationParams = {
        ...reservation,
        ...voucherPrice,
        ...pickupStoreData,
        resource_id: product.id,
        quantity: product.quantity,
      }
      const resp = PlanyoService.makeReservation(reservationParams)

      allReservations.push(resp)
    })

    extraProducts.forEach((product) => {
      const reservationParams = {
        ...reservation,
        ...pickupStoreData,
        resource_id: product.id,
        quantity: product.quantity,
      }
      const resp = PlanyoService.makeReservation(reservationParams)

      allReservations.push(resp)
    })

    const allResp = await Promise.all(allReservations)

    return allResp
  },
  async validateVoucherCode({ state, rootState, commit }) {
    let resp
    const params = {
      check_reservation_time: true,
      language: rootState.user.currentLanguage.toUpperCase(),
      voucher_code_prefix: state.voucherCode,
    }

    const respCodes = await PlanyoService.validateVoucher(params)

    const code = respCodes?.data?.results && respCodes?.data?.results[state.voucherCode.toLowerCase()]

    if (!code) {
      resp = { message: 'Code is not valid', status: 0 }
    } else {
      commit('setVoucher', code)
      resp = { message: 'Voucher code has been applied', code, status: 1 }
    }

    return resp
  },
  setOrderStep({ commit }, step) {
    commit('setOrderStep', step)
  },
  setChildStepBySlug({ commit, state }, slug) {
    const step = state.orderSteps[state.orderStep].children.findIndex((v) => v.value === slug)
    commit('setChildOrderStep', step)
  },
  nextOrderStep({ state, commit, getters }) {
    if (getters.isNextStepIsValid) {
      const step = state.orderStep + 1 < state.orderSteps.length ? state.orderStep + 1 : state.orderStep

      const url = new URL(window.location)
      url.searchParams.set('step', step)
      window.history.pushState({}, '', url)

      commit('setOrderStep', step)
    }
  },
  prevOrderStep({ state, commit }) {
    const step = state.orderStep - 1 > 0 ? state.orderStep - 1 : state.orderStep

    const url = new URL(window.location)
    url.searchParams.set('step', step)
    window.history.pushState({}, '', url)

    commit('setOrderStep', step)
  },
  setOrderStepBySlug({ state, commit }, slug) {
    const step = state.orderSteps.findIndex((v) => v.value === slug)

    commit('setOrderStep', step)
  },
  setMovingType({ commit }, type) {
    commit('setMovingType', type)
  },
  setStartDate({ commit }, date) {
    commit('setStartDate', date)
  },
  setAmountOfWeeks({ commit }, number) {
    commit('setAmountOfWeeks', number)
  },
  updateCart({ state, commit }, { product, quantity }) {
    const cartItem = state.items.find((v) => v.id === product.id)
    if (quantity > 0) {
      if (!cartItem) {
        commit('pushProductToCart', { id: product.id, quantity: quantity })
      } else {
        commit('updateQuantity', { id: product.id, quantity: quantity })
      }
    } else {
      if (cartItem) {
        commit('removeProductFromCart', { ...product })
      }
    }
  },
  setDeliveryCosts({ state, rootState, getters, commit }) {
    const type = state.deliveryType
    const deliveryId = rootState.products.deliveryId
    let productArray = [],
      product,
      quantity = 1

    const pickupName = rootState.storages.settings.pickUpStorageTypeNew[rootState.user.shippingCountry.toLowerCase()]

    if (type === 'delivery') {
      product = rootState.products.delivery.find((v) => v.id === deliveryId['delivery'])
      quantity = Math.ceil(getters.capacityPoints / 100)
    }

    if (type === 'pickup' && state.pickUpPlaceType === pickupName && state.pickUpStore.isKiosk) {
      product = rootState.products.delivery.find((v) => v.id === deliveryId['pickup'])
      quantity = getters.pickupPoints
    }

    if (product) {
      productArray.push({ ...product, id: product.id, quantity: quantity })
    }

    commit('setDeliveryProduct', productArray)
  },
  changeDeliveryType({ commit, dispatch, rootState }, type) {
    const defaultDeliveryStoreType =
      rootState.storages.settings.deliveryStorageTypeNew[rootState.user.shippingCountry.toLowerCase()]
    commit('setDeliveryType', type)
    if (type === 'delivery') {
      commit('setPickUpPlaceType', defaultDeliveryStoreType)
      commit('setDeliveryPlaceID', null)
    } else {
      commit('setPickUpPlaceType', null)
    }
    dispatch('setDeliveryCosts')
  },
  setPickUpPlaceType({ commit, dispatch }, pickUpPlaceType) {
    commit('setPickUpPlaceType', pickUpPlaceType)
    dispatch('setDeliveryCosts')
  },
  setPickUpPlace({ commit, dispatch }, pickUpPlace) {
    commit('setPickupStore', pickUpPlace)
    commit('setPickUpPlace', pickUpPlace.name)
    dispatch('setPickUpPlaceType', pickUpPlace.type)
    commit('setDeliveryPlaceID', pickUpPlace.id)
  },
}

// mutations
const mutations = {
  setRemoteCartId(state, id) {
    state.remoteId = id
  },
  setVoucherCode(state, code) {
    state.voucherCode = code
  },
  setVoucher(state, code) {
    state.voucher = code
  },
  setReservationsResponse(state, arr) {
    state.reservationsResponse = arr
  },
  setOrderStep(state, step) {
    state.orderStep = step
    state.orderChildStep = 0
  },
  setChildOrderStep(state, step) {
    state.orderChildStep = step
  },
  setDeliveryType(state, type) {
    state.deliveryType = type
  },
  setDeliveryPlaceID(state, id) {
    state.deliverPlaceID = id
  },
  setMovingType(state, type) {
    state.movingType = type
  },
  setStartDate(state, date) {
    state.startDate = date
  },
  setAmountOfWeeks(state, amount) {
    state.amountOfWeeks = amount
  },
  setPickUpPlaceType(state, pickUpPlaceType) {
    state.pickUpPlaceType = pickUpPlaceType
  },
  setPickUpPlace(state, pickUpPlace) {
    state.pickUpPlace = pickUpPlace
  },
  setPickupStore(state, store) {
    state.pickUpStore = store
  },
  setDeliveryProduct(state, deliveryProduct) {
    state.deliveryProduct = deliveryProduct
  },
  pushProductToCart(state, product) {
    state.items.push(product)
  },
  clearCart(state) {
    state.items = []
  },
  removeProductFromCart(state, product) {
    const cartItemIndex = state.items.findIndex((item) => item.id === product.id)
    state.items.splice(cartItemIndex, 1)
  },
  updateQuantity(state, product) {
    const cartItem = state.items.find((item) => item.id === product.id)
    cartItem.quantity = product.quantity
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
